import { useCallback, useEffect, useState } from "react";
import { logError, LogCategory } from "utils/sentryLogger";
import { fetchCustomers, fetchSchools, fetchTeachers } from "./csReportsApi";

const formatTeacherLabel = (teacher) => {
  const name = [teacher.first_name, teacher.last_name].filter(Boolean).join(" ").trim();
  return name ? `${name} (${teacher.email})` : teacher.email;
};

export const useCSReportsFilters = () => {
  const [customers, setCustomers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [teachers, setTeachers] = useState([]);

  const [customer, setCustomer] = useState(null);
  const [school, setSchool] = useState(null);
  const [teacher, setTeacher] = useState(null);
  const [standard, setStandard] = useState(null);

  const [customersLoading, setCustomersLoading] = useState(false);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [teachersLoading, setTeachersLoading] = useState(false);

  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setCustomersLoading(true);
    fetchCustomers()
      .then((rows) => {
        if (cancelled) return;
        setCustomers(
          rows.map((r) => ({
            value: r.id,
            label: r.name,
            name: r.name,
            client_type: r.client_type,
          }))
        );
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        logError("CS Reports: failed to load customers", e, {
          category: LogCategory.ERROR,
        });
      })
      .finally(() => {
        if (!cancelled) setCustomersLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setSchools([]);
    setSchool(null);
    setTeachers([]);
    setTeacher(null);
    setStandard(null);
    if (!customer) return undefined;
    let cancelled = false;
    setSchoolsLoading(true);
    fetchSchools(customer.value)
      .then((rows) => {
        if (cancelled) return;
        setSchools(
          rows.map((r) => ({
            value: r.id,
            label: [r.name, r.city, r.state].filter(Boolean).join(" — "),
            name: r.name,
          }))
        );
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        logError("CS Reports: failed to load schools", e, {
          category: LogCategory.ERROR,
        });
      })
      .finally(() => {
        if (!cancelled) setSchoolsLoading(false);
      });
    return () => { cancelled = true; };
  }, [customer]);

  useEffect(() => {
    setTeachers([]);
    setTeacher(null);
    if (!customer) return undefined;
    let cancelled = false;
    setTeachersLoading(true);
    fetchTeachers({ customerId: customer.value, schoolId: school?.value })
      .then((rows) => {
        if (cancelled) return;
        setTeachers(
          rows.map((r) => ({
            value: r.id,
            label: formatTeacherLabel(r),
            email: r.email,
          }))
        );
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        logError("CS Reports: failed to load teachers", e, {
          category: LogCategory.ERROR,
        });
      })
      .finally(() => {
        if (!cancelled) setTeachersLoading(false);
      });
    return () => { cancelled = true; };
  }, [customer, school]);

  const reset = useCallback(() => {
    setCustomer(null);
    setSchool(null);
    setTeacher(null);
    setStandard(null);
  }, []);

  return {
    customers,
    schools,
    teachers,
    customer,
    school,
    teacher,
    standard,
    setCustomer,
    setSchool,
    setTeacher,
    setStandard,
    customersLoading,
    schoolsLoading,
    teachersLoading,
    error,
    reset,
  };
};
